import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  FloatingArrow,
  arrow,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  useTransitionStyles
} from '@floating-ui/react'
import classnames from 'classnames'

import { lightGrey, mediumGrey } from '../../styles/const/colors'
import useStyles from './style'

const arrowHeight = 6
const defaultGap = 2
const arrowOffset = arrowHeight + defaultGap
const borderColor = lightGrey

function Tooltip({
  triggerElement,
  placement = 'top',
  children,
  // style
  className,
  bodyClassName,
  color = mediumGrey,
  backgroundColor = 'white',
  triggerClassName,
  offsetSize = 0
}) {
  const [isOpen, setIsOpen] = useState(false)
  const arrowRef = useRef(null)

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    placement,
    middleware: [
      offset(arrowOffset + offsetSize),
      arrow({ element: arrowRef }),
      flip({ fallbackAxisSideDirection: 'start' }),
      shift()
    ]
  })

  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    // Configure both open and close durations:
    duration: 300,
    initial: {
      opacity: 0,
      top: '10px'
    },
    open: ({ side }) => ({
      opacity: 1,
      top: '0px'
    })
  })

  // Event listeners to change the open state
  const hover = useHover(context, { move: false })
  const focus = useFocus(context)
  // adds the ability to dismiss the tooltip when the user presses the esc key.
  const dismiss = useDismiss(context)
  // Role props for screen readers
  const role = useRole(context, { role: 'tooltip' })

  // Merge all the interactions into prop getters
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role])
  const classes = useStyles({ backgroundColor, color, borderColor })

  return (
    <div className={classnames('tooltip', className)}>
      <div
        className={classnames(triggerClassName, classes.triggerElement)}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        {triggerElement}
      </div>
      {/* Tooltip body is not rendered until it will be opened to avoid re-rendering on scroll
      and adding horizontal scrolls when it is outside the isOpen container like in table  */}
      {isMounted && (
        <div
          ref={refs.setFloating}
          style={{ ...floatingStyles, ...transitionStyles }}
          className={classnames(classes.tooltipBody, bodyClassName)}
          {...getFloatingProps()}
        >
          <div>
            <FloatingArrow
              ref={arrowRef}
              context={context}
              width={12}
              height={arrowHeight}
              tipRadius={2}
              strokeWidth={1}
              stroke={borderColor}
              fill={backgroundColor}
            />
            {children}
          </div>
        </div>
      )}
    </div>
  )
}

export default Tooltip

Tooltip.propTypes = {
  className: PropTypes.string,
  bodyClassName: PropTypes.string,
  triggerClassName: PropTypes.string,
  color: PropTypes.string,
  backgroundColor: PropTypes.string,
  placement: PropTypes.string,
  offsetSize: PropTypes.number,
  triggerElement: PropTypes.node,
  children: PropTypes.node.isRequired
}
